import { fetchCollectionProductCounts, fetchShopifyMenu } from './shopifyClient';

// Interface for Shopify menu items
interface ShopifyMenuItem {
  id: string;
  title: string;
  url: string;
  items?: ShopifyMenuItem[];
}

// Interface for Shopify menu
interface ShopifyMenu {
  id: string;
  handle: string;
  title: string;
  items: ShopifyMenuItem[];
}

// Extract collection handle from URL
const extractCollectionHandle = (url: string): string => {
  if (!url) return '';
  
  // URLs can be in different formats:
  // 1. /collections/collection-handle
  // 2. /collections/collection-handle/products
  // 3. /pages/page-handle
  // 4. /products/product-handle
  
  // Remove any query parameters
  const urlWithoutParams = url.split('?')[0];
  
  // Split the URL into parts
  const parts = urlWithoutParams.split('/').filter(part => part.length > 0);
  
  // Check if it's a collection URL
  if (parts.length >= 2 && parts[0] === 'collections') {
    return parts[1]; // Return the collection handle
  }
  
  // For other URL types, use the last meaningful part
  if (parts.length > 0) {
    return parts[parts.length - 1];
  }
  
  return '';
};

// Transform Shopify menu items into CategoryTreeNode format
const transformMenuItemToCategoryNode = (
  menuItem: ShopifyMenuItem, 
  parentId: number | null = null,
  idCounter: { value: number } = { value: 1 },
  productCountMap: Record<string, number> = {}
): CategoryTreeNode => {
  // Extract collection handle from URL
  const urlSlug = extractCollectionHandle(menuItem.url);
  
  // Create a unique ID for this node
  const nodeId = idCounter.value++;
  
  // Get the product count for this collection if available
  const productCount = productCountMap[urlSlug] || 0;
  
  // console.log(`Menu item: ${menuItem.title}, URL: ${menuItem.url}, Extracted slug: ${urlSlug}, Product count: ${productCount}`);
  
  // Create the category node
  const categoryNode: CategoryTreeNode = {
    crawler_id: nodeId,
    id: nodeId,
    name: menuItem.title,
    url_slug: urlSlug,
    parent_crawler_id: parentId,
    children: [],
    total_count: productCount // Set the product count from Shopify
  };
  
  // Process child items if they exist
  if (menuItem.items && menuItem.items.length > 0) {
    categoryNode.children = menuItem.items.map(childItem => 
      transformMenuItemToCategoryNode(childItem, nodeId, idCounter, productCountMap)
    );
  }
  
  return categoryNode;
};

// Transform Shopify menu into CategoryTreeNode format
export const transformShopifyMenuToCategories = async (menuHandle: string = 'main-menu'): Promise<CategoryTreeNode[]> => {
  try {
    // Fetch the menu from Shopify
    const menu = await fetchShopifyMenu(menuHandle);
    
    if (!menu || !menu.items || menu.items.length === 0) {
      // console.log('No menu items found');
      return [];
    }
    
    // Fetch collection product counts
    const productCountMap = await fetchCollectionProductCounts();
    // console.log('Product count map:', productCountMap);
    
    // Transform menu items to category nodes
    const idCounter = { value: 1 }; // Counter to generate unique IDs
    const categoryTree = menu.items.map(item => 
      transformMenuItemToCategoryNode(item, null, idCounter, productCountMap)
    );
    
    // console.log(`Transformed ${categoryTree.length} menu items to category nodes`);
    // console.log('First level categories:', categoryTree.map(cat => ({
    //   name: cat.name,
    //   url_slug: cat.url_slug,
    //   total_count: cat.total_count,
    //   children_count: cat.children?.length || 0
    // })));
    
    return categoryTree;
  } catch (error) {
    console.error('Error transforming Shopify menu to categories:', error);
    return [];
  }
};
